<template>
  <div class="fallback-container">
    <!-- Pantalla para In-App Browsers (WhatsApp, Instagram, TikTok, Facebook, …) -->
    <div
      v-if="isInAppBrowser"
      class="hero-screen"
    >
      <!-- Background image with overlay -->
      <div class="hero-bg">
        <img
          src="/hero-bg.png"
          alt=""
          class="hero-bg__img"
        />
        <div class="hero-bg__overlay" />
      </div>

      <!-- Top navbar -->
      <nav class="hero-nav">
        <img
          src="/fanaty-logo-purple.png"
          alt="Fanaty"
          class="hero-nav__logo"
        />
      </nav>

      <!-- Hero content -->
      <div class="hero-content">
        <h1 class="hero-title">
          <span class="hero-title__white">{{ $t('hero.title_line1') }}</span>
          <span class="hero-title__purple">{{ $t('hero.title_line2') }}</span>
        </h1>

        <button
          class="hero-btn"
          @click="openApp"
        >
          {{ isIOS ? $t('hero.go_to_app') : $t('hero.download_app') }}
        </button>

        <!-- Salida siempre visible: si el salto a la app no prende, la tienda -->
        <button
          v-if="isIOS"
          class="hero-link"
          @click="goToStore"
        >
          {{ $t('hero.download_app') }}
        </button>
      </div>
    </div>

    <!-- Redirección automática (sólo mientras dura el salto) -->
    <div
      v-else-if="autoRedirecting"
      class="loading-screen"
    >
      <img
        src="/logo-rounded-516_7C3AED.png"
        alt="Fanaty"
        class="logo-sm"
      />
      <div class="spinner" />
      <span class="loading-text">{{ $t('hero.redirecting') }}</span>
    </div>

    <!-- Pantalla de acción: NUNCA un spinner sin salida -->
    <div
      v-else
      class="action-screen"
    >
      <img
        src="/logo-rounded-516_7C3AED.png"
        alt="Fanaty"
        class="logo-md"
      />

      <h1 class="action-title">
        {{ hasDeepLink ? $t('hero.link_title') : $t('hero.title_line1') }}
      </h1>
      <p class="action-subtitle">
        {{ hasDeepLink ? $t('hero.link_subtitle') : $t('hero.title_line2') }}
      </p>

      <button
        v-if="opensApp"
        class="hero-btn action-btn"
        @click="openApp"
      >
        {{ $t('hero.open_in_app') }}
      </button>

      <button
        class="hero-btn action-btn"
        :class="{ 'hero-btn--ghost': opensApp }"
        @click="goToStore"
      >
        {{ $t('hero.download_app') }}
      </button>
    </div>
  </div>
</template>

<script>
const APP_STORE_URL = 'https://apps.apple.com/us/app/fanaty/id1461051554'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.fanaty'
const WEB_URL = 'https://www.fanaty.com/videos'

// Rutas que la app sabe abrir (mismo set que el AASA y que `linking.ts` del mobile).
const DEEP_LINK_PREFIXES = ['/l/', '/g/', '/video_detail/', '/match_detail/']

// Links de carteles/QR viejos impresos: no son rutas de la app ni están en el AASA,
// pero quien los escanea quiere abrir la app (home), no ir a la tienda.
const LEGACY_APP_PATHS = ['/views/facility-list', '/views/public-facily-list', '/views/private-facily-list']

// Cuánto esperamos a que el SO se lleve la pestaña antes de mostrar los botones.
const AUTO_REDIRECT_WATCHDOG_MS = 2500

// Cuánto esperamos al esquema `fanaty://` antes de caer a la tienda.
const SCHEME_FALLBACK_MS = 2000

export default {
  name: 'FallbackLinks',

  data() {
    return {
      isInAppBrowser: false,
      isIOS: false,
      isAndroid: false,

      // Arranca en true sólo si de verdad vamos a saltar solos; el watchdog lo
      // baja para que la pantalla siempre termine con botones.
      autoRedirecting: false,

      watchdogTimer: null,
      schemeFallbackTimer: null,
      onVisibilityChange: null,
      onPageHide: null,
    }
  },

  computed: {
    // El deep link original. nginx redirige /l/... a /fallback_links?deepLink=/l/...
    // así que el destino llega por query; el fullPath es el fallback.
    deepLinkPath() {
      const { deepLink } = this.$route.query
      const raw = deepLink || this.$route.fullPath

      return decodeURIComponent(String(raw || ''))
        .replace(/^https?:\/\/[^/]+/i, '') // soporta deep links absolutos
        .replace(/^\/*/, '/') // normaliza a una sola barra inicial
    },

    hasDeepLink() {
      return DEEP_LINK_PREFIXES.some(prefix => this.deepLinkPath.startsWith(prefix))
    },

    isLegacyAppLink() {
      const [path] = this.deepLinkPath.split(/[?#]/)

      return LEGACY_APP_PATHS.includes(path.replace(/\/+$/, ''))
    },

    // Deep link real o cartel viejo: en ambos casos intentamos abrir la app.
    opensApp() {
      return this.hasDeepLink || this.isLegacyAppLink
    },

    // Custom URL Scheme: la ÚNICA forma de abrir la app cuando el universal link
    // no disparó (navegador interno de WhatsApp, o la preferencia por dominio de
    // iOS de quedarse en Safari).
    schemeUrl() {
      if (this.isLegacyAppLink) return 'fanaty://'

      return `fanaty://${this.deepLinkPath.replace(/^\/+/, '')}`
    },

    // URL de la página intermedia que se abre en Chrome/Safari (fuera del WebView)
    redirectPageUrl() {
      return `https://app.fanaty.com/deep-link-redirect?deepLink=${encodeURIComponent(this.deepLinkPath)}`
    },

    // intent:// abre la app si está instalada y cae solo al Play Store si no.
    // La query se conserva (`/l/r/:id?highlights=1` la necesita); lo único que
    // hay que sacar es un fragmento, que chocaría con el `#Intent;`.
    intentUrl() {
      const [cleanPath] = this.deepLinkPath.split('#')
      const fallback = encodeURIComponent(PLAY_STORE_URL)

      // El intent-filter https de la app sólo cubre /l — un cartel viejo va por el
      // esquema fanaty://, que abre la home.
      if (this.isLegacyAppLink) {
        return `intent://#Intent;scheme=fanaty;package=com.fanaty;S.browser_fallback_url=${fallback};end`
      }

      return `intent://app.fanaty.com${cleanPath}#Intent;scheme=https;package=com.fanaty;S.browser_fallback_url=${fallback};end`
    },

    storeUrl() {
      if (this.isIOS) return APP_STORE_URL
      if (this.isAndroid) return PLAY_STORE_URL

      return WEB_URL
    },
  },

  created() {
    const ua = navigator.userAgent || navigator.vendor || window.opera

    const isInstagram = /Instagram/i.test(ua)
    const isTikTok = /TikTok|musical_ly|BytedanceWebview/i.test(ua)
    const isFacebook = /FBAN|FBAV|FB_IAB|FacebookExternalHit/i.test(ua)

    // WhatsApp faltaba: sus links caían por el camino "normal" y, dentro de su
    // WebView, el salto a la tienda queda bloqueado sin diálogo — el spinner
    // eterno que reportaron.
    const isWhatsApp = /WhatsApp/i.test(ua)
    const isTelegram = /Telegram/i.test(ua)
    const isOtherWebView = /Line\/|Snapchat|Twitter|LinkedInApp|GSA\//i.test(ua)

    this.isInAppBrowser = isInstagram || isTikTok || isFacebook || isWhatsApp || isTelegram || isOtherWebView
    this.isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
    this.isAndroid = /android/i.test(ua)

    // Detectar idioma del teléfono para in-app browsers si no hay preferencia previa
    if (!localStorage.getItem('lang')) {
      const browserLang = (navigator.language || '').split('-')[0]
      this.$i18n.locale = browserLang === 'es' ? 'es' : 'en'
    }

    if (this.isInAppBrowser) {
      // Android in-app browser: intent:// abre la app sola (y cae al store si no está)
      if (this.isAndroid) window.location.href = this.intentUrl

      return
    }

    this.startAutoRedirect()
  },

  beforeDestroy() {
    this.clearTimers()
  },

  methods: {
    // Qué hacemos solos al entrar, por plataforma:
    //  · Android con deep link → intent:// (abre la app o cae al Play Store)
    //  · Escritorio, o sin deep link (QR /download) → tienda / web
    //  · Los carteles viejos (/views/facility-list) cuentan como deep link a la home
    //  · iOS con deep link → NADA: `fanaty://` sin gesto del usuario dispara un
    //    diálogo y, si no está instalada, un error feo. Mostramos los botones.
    startAutoRedirect() {
      if (this.isAndroid && this.opensApp) {
        this.autoRedirecting = true
        this.armWatchdog()
        window.location.href = this.intentUrl

        return
      }

      if (this.opensApp && this.isIOS) return

      this.autoRedirecting = true
      this.armWatchdog()
      window.location.replace(this.storeUrl)
    },

    // Si a los pocos segundos seguimos acá, el salto no prendió (o lo cancelaron):
    // se muestran los botones en vez de dejar el spinner girando para siempre.
    armWatchdog() {
      this.clearTimeoutRef('watchdogTimer')
      this.watchdogTimer = setTimeout(() => {
        this.autoRedirecting = false
      }, AUTO_REDIRECT_WATCHDOG_MS)
    },

    goToStore() {
      window.location.href = this.storeUrl
    },

    openApp() {
      if (this.isIOS) {
        // iOS: window.open está bloqueado en WebViews de WhatsApp/Instagram/TikTok
        // y los Universal Links tampoco disparan ahí (WKWebView de Apple).
        // El Custom URL Scheme es el único camino que queda.
        window.location.href = this.schemeUrl

        // Si la app no está instalada, después de 2s ir al App Store
        this.clearTimeoutRef('schemeFallbackTimer')
        this.schemeFallbackTimer = setTimeout(() => {
          window.location.href = APP_STORE_URL
        }, SCHEME_FALLBACK_MS)

        // Si la app se abrió, cancelar el fallback para q no vaya al store cuando vuelva
        this.onVisibilityChange = () => {
          if (document.hidden) this.clearTimers()
        }
        this.onPageHide = () => {
          this.clearTimers()
        }
        document.addEventListener('visibilitychange', this.onVisibilityChange)
        window.addEventListener('pagehide', this.onPageHide)

        return
      }

      if (this.isAndroid) {
        // intent:// abre la app y, si no está, cae solo al Play Store
        window.location.href = this.opensApp ? this.intentUrl : PLAY_STORE_URL

        return
      }

      window.location.href = WEB_URL
    },

    clearTimeoutRef(key) {
      if (this[key]) {
        clearTimeout(this[key])
        this[key] = null
      }
    },

    clearTimers() {
      this.clearTimeoutRef('watchdogTimer')
      this.clearTimeoutRef('schemeFallbackTimer')

      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
        this.onVisibilityChange = null
      }
      if (this.onPageHide) {
        window.removeEventListener('pagehide', this.onPageHide)
        this.onPageHide = null
      }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.fallback-container {
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #0a0a14;
}

/* ── Hero Screen (In-App Browser) ── */
.hero-screen {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Background image */
.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-bg__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.hero-bg__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 20, 0.45) 0%,
    rgba(10, 10, 20, 0.6) 40%,
    rgba(10, 10, 20, 0.85) 70%,
    rgba(10, 10, 20, 0.95) 100%
  );
}

/* Navbar */
.hero-nav {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 20px 24px;
}

.hero-nav__logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

/* Hero Content */
.hero-content {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 24px 60px;
  max-width: 720px;
}

.hero-title {
  font-size: 48px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -1.5px;
  margin-bottom: 24px;
}

.hero-title__white {
  display: block;
  color: #ffffff;
}

.hero-title__purple {
  display: block;
  color: #8b5cf6;
}

.hero-description {
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 480px;
  margin-bottom: 32px;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 40px;
  background: #8b5cf6;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  letter-spacing: 0.3px;
  width: fit-content;
  box-shadow: 0 4px 24px rgba(139, 92, 246, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.2s ease;
}

.hero-btn:hover {
  background: #7c3aed;
  box-shadow: 0 6px 32px rgba(139, 92, 246, 0.5);
}

.hero-btn:active {
  transform: scale(0.97);
  box-shadow: 0 2px 16px rgba(139, 92, 246, 0.35);
}

.hero-btn--ghost {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: none;
}

.hero-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

.hero-link {
  margin-top: 16px;
  padding: 8px 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
  width: fit-content;
}

/* ── Loading Screen ── */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 20px;
}

.logo-sm {
  width: 80px;
  object-fit: contain;
  opacity: 0.9;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.3px;
}

/* ── Action Screen (nunca un callejón sin salida) ── */
.action-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 32px 24px;
  text-align: center;
}

.logo-md {
  width: 96px;
  object-fit: contain;
  margin-bottom: 28px;
}

.action-title {
  font-size: 26px;
  font-weight: 900;
  line-height: 1.2;
  letter-spacing: -0.5px;
  color: #ffffff;
  margin-bottom: 12px;
  max-width: 420px;
}

.action-subtitle {
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.6;
  max-width: 380px;
  margin-bottom: 32px;
}

.action-btn {
  width: 100%;
  max-width: 340px;
}

.action-btn + .action-btn {
  margin-top: 12px;
}

/* ── Responsive ── */
@media (max-width: 480px) {
  .hero-title {
    font-size: 36px;
    letter-spacing: -1px;
  }

  .hero-description {
    font-size: 15px;
  }

  .hero-btn {
    width: 100%;
  }
}
</style>
